import { useState, useCallback, useRef, useEffect, useMemo, CSSProperties } from 'react';
import { userAppContext } from '@/context/Context';
import { encodeData } from '@/utils/encodeUtils';
import { EnrichmentOrder, EnrichmentOrderBy, EnrichmentPoint } from '../types';
import {
  DEFAULT_ENRICHMENT_ORDER,
  DEFAULT_ENRICHMENT_ORDER_BY,
  defaultEnrichmentOrderForColumn,
} from '../helpers';
import { trackEvent } from '@/utils/analytics';

function openSearchInNewTab(search: string) {
  const url = `${window.location.origin}/search${search}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export interface EnrichmentDataTableProps {
  data: EnrichmentPoint[];
  isDownload?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: string[]) => void;
  onDiseaseClick?: (item: EnrichmentPoint) => void;
  onIntersectionClick?: (item: EnrichmentPoint) => void;
  geneFilterType?: string;
  searchType?: string;
  orderBy?: EnrichmentOrderBy;
  order?: EnrichmentOrder;
  onSortChange?: (orderBy: EnrichmentOrderBy, order: EnrichmentOrder) => void;
}

function getGeneFilterName(geneFilterType?: string): string {
  switch (geneFilterType) {
    case 'geneId':
      return 'geneNcbiID';
    case 'ensemblId':
      return 'geneEnsemblIDs';
    case 'uniprotId':
      return 'geneProteinStrIDs';
    case 'geneSymbol':
    case 'name':
    default:
      return 'geneSymbol';
  }
}

function getDiseaseIdent(item: EnrichmentPoint): string | null {
  if (item.diseaseUMLSCUI?.trim()) return item.diseaseUMLSCUI.trim();
  if (item.diseaseName?.trim()) return item.diseaseName.trim();
  return null;
}

function matchesDiseaseQuery(item: EnrichmentPoint, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (item.diseaseName.toLowerCase().includes(q)) return true;
  return (item.diseaseUMLSCUI ?? '').toLowerCase().includes(q);
}

const ITEMS_PER_PAGE = 20;

const DataTable = ({
  data,
  isDownload = false,
  selectedIds,
  onSelectionChange,
  onDiseaseClick,
  onIntersectionClick,
  geneFilterType,
  searchType,
  orderBy = DEFAULT_ENRICHMENT_ORDER_BY,
  order = DEFAULT_ENRICHMENT_ORDER,
  onSortChange,
}: EnrichmentDataTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [diseaseQuery, setDiseaseQuery] = useState('');
  const context = userAppContext();

  const canSort = !isDownload && onSortChange != null;

  const handleSortColumn = useCallback(
    (column: EnrichmentOrderBy) => {
      if (!onSortChange) return;
      setCurrentPage(1);
      if (orderBy === column) {
        onSortChange(column, order === 'ASC' ? 'DESC' : 'ASC');
        return;
      }
      onSortChange(column, defaultEnrichmentOrderForColumn(column));
    },
    [onSortChange, orderBy, order],
  );

  const filteredData = useMemo(
    () => (isDownload ? data : data.filter(item => matchesDiseaseQuery(item, diseaseQuery))),
    [data, diseaseQuery, isDownload],
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(prev => Math.min(prev, totalPages));
  }, [totalPages]);

  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = (safePage - 1) * ITEMS_PER_PAGE;
  const indexOfLastItem = safePage * ITEMS_PER_PAGE;
  const currentItems = isDownload ? filteredData : filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const isInteractive =
    !isDownload && (onSelectionChange != null || onDiseaseClick != null || onIntersectionClick != null);
  const showCheckboxes = !isDownload && onSelectionChange != null && selectedIds != null;

  const selectedSet = selectedIds ?? new Set<string>();
  const allOnPageSelected = currentItems.length > 0 && currentItems.every(item => selectedSet.has(item.id));
  const someOnPageSelected = currentItems.some(item => selectedSet.has(item.id));
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
  }, [someOnPageSelected, allOnPageSelected]);

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allOnPageSelected) {
      const removed = new Set(currentItems.map(i => i.id));
      onSelectionChange([...selectedSet].filter(id => !removed.has(id)));
    } else {
      const addIds = currentItems.map(i => i.id);
      onSelectionChange([...new Set([...selectedSet, ...addIds])]);
    }
  }, [onSelectionChange, currentItems, selectedSet, allOnPageSelected]);

  const handleSelectRow = useCallback(
    (item: EnrichmentPoint) => {
      if (!onSelectionChange) return;
      const next = new Set(selectedSet);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      onSelectionChange([...next]);
    },
    [onSelectionChange, selectedSet],
  );

  const handleDiseaseClick = useCallback(
    (item: EnrichmentPoint) => {
      if (isDownload) return;
      trackEvent({
        name: 'navigation_click',
        buttonId: 'enrichment_table_disease_click',
        label: 'Open disease summary from enrichment',
        page: 'enrichment',
        section: 'results_table',
        feature: 'navigation',
        eventCategory: 'enrichment',
      });
      onDiseaseClick?.(item);
      const ctx = context as { restrictSources?: string | false } | null;
      const source = ctx?.restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      const isVariantEnrichment =
        typeof item.variantRatio === 'string' && item.variantRatio.trim().length > 0;
      const variants = item.intersection;

      if (isVariantEnrichment) {
        if (!Array.isArray(variants) || variants.length === 0) return;
        openSearchInNewTab(
          `?view=VARIANTS&idents=${variants.map(variant => encodeURIComponent(variant)).join('-')}&source=${source}&tab=VARIANTS`,
        );
        return;
      }

      const ident = getDiseaseIdent(item);
      if (!ident) return;
      openSearchInNewTab(`?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=GDA`);
    },
    [isDownload, onDiseaseClick, context],
  );

  const handleIntersectionClick = useCallback(
    (item: EnrichmentPoint) => {
      if (isDownload) return;
      trackEvent({
        name: 'navigation_click',
        buttonId: 'enrichment_table_intersection_click',
        label: 'Open intersection summary from enrichment',
        page: 'enrichment',
        section: 'results_table',
        feature: 'navigation',
        eventCategory: 'enrichment',
      });
      onIntersectionClick?.(item);
      const ctx = context as { restrictSources?: string | false } | null;
      const source = ctx?.restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      const isVariantEnrichment =
        typeof item.variantRatio === 'string' && item.variantRatio.trim().length > 0;
      const variants = (item as EnrichmentPoint & { intersection?: string[] })?.intersection;

      if (isVariantEnrichment) {
        if (!Array.isArray(variants) || variants.length === 0) return;
        const ident = getDiseaseIdent(item);
        if (!ident) return;
        const filterSummary = [
          {
            name: 'variant',
            operator: 'eq' as const,
            type: 'string' as const,
            value: [...variants],
            isEvidence: false,
          },
        ];
        const fEncoded = encodeData(filterSummary);
        openSearchInNewTab(
          `?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=VDA&f=${fEncoded}`,
        );
        return;
      }

      const ident = getDiseaseIdent(item);
      if (!ident) return;

      const genes = (item as EnrichmentPoint & { intersection?: string[] })?.intersection;
      if (Array.isArray(genes) && genes.length > 0) {
        const filterSummary = [
          {
            name: getGeneFilterName(geneFilterType),
            operator: 'eq' as const,
            type: 'string' as const,
            value: [...genes],
            isEvidence: false,
          },
        ];
        const fEncoded = encodeData(filterSummary);
        openSearchInNewTab(
          `?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=GDA&f=${fEncoded}`,
        );
        return;
      }

      openSearchInNewTab(`?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=GDA`);
    },
    [isDownload, onIntersectionClick, context, geneFilterType],
  );

  const title = isDownload ? 'Top Most Significant Results' : '';
  const isVariantEnrichment = searchType === 'VARIANTS';
  const ratioHeaderLabel = isVariantEnrichment ? 'Variant Ratio' : 'Gene Ratio';
  const containerClass = isDownload
    ? 'bg-gray-50 p-4 rounded-lg border border-gray-300'
    : 'bg-gray-50 p-4 rounded-lg border border-gray-300 mt-4';

  const diseaseColumnStyle = isVariantEnrichment
    ? { width: 220, maxWidth: 220 }
    : { width: 250, maxWidth: 250 };

  const headerCellClass = (align: 'left' | 'right', isLast = false) =>
    `${align === 'left' ? 'text-left' : 'text-right'} py-2 px-3 font-bold ${isLast ? '' : 'border-r border-gray-300'}`;

  const plainHeader = (
    label: string,
    align: 'left' | 'right',
    isLast = false,
    style?: CSSProperties,
  ) => (
    <th className={headerCellClass(align, isLast)} style={style}>
      <span className="text-xs">{label}</span>
    </th>
  );

  const sortableHeader = (
    column: EnrichmentOrderBy,
    label: string,
    align: 'left' | 'right',
    isLast = false,
  ) => {
    const active = orderBy === column;
    const ariaSort =
      active && order === 'ASC' ? 'ascending' : active && order === 'DESC' ? 'descending' : 'none';
    if (!canSort) {
      return (
        <th className={headerCellClass(align, isLast)} aria-sort={ariaSort}>
          <span className="text-xs">{label}</span>
        </th>
      );
    }
    return (
      <th
        className={`${align === 'left' ? 'text-left' : 'text-right'} py-0 px-0 font-bold ${isLast ? '' : 'border-r border-gray-300'}`}
        aria-sort={ariaSort}
      >
        <button
          type="button"
          onClick={() => handleSortColumn(column)}
          className={`w-full py-2 px-3 font-bold flex items-center gap-1 min-h-[2.5rem] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${
            align === 'left' ? 'justify-start text-left' : 'justify-end text-right'
          }`}
          aria-label={`Sort by ${label}`}
        >
          <span className="text-xs">{label}</span>
          {active && (
            <span aria-hidden className="inline-block min-w-[1ch] text-xs">
              {order === 'ASC' ? '↑' : '↓'}
            </span>
          )}
        </button>
      </th>
    );
  };

  return (
    <div className={containerClass}>
      {title && <h3 className="text-lg font-bold text-gray-700 mb-4 text-center">{title}</h3>}
      {!isDownload && (
        <div className="mb-3">
          {showCheckboxes && (
            <p className="text-sm text-gray-600 mb-3">
              Select diseases to display in the graph above. Click a disease name or intersection to open the
              Disease Summary view in a new tab.
            </p>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="font-medium whitespace-nowrap">Search disease</span>
            <input
              type="search"
              value={diseaseQuery}
              onChange={e => {
                setDiseaseQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Name or CUI"
              aria-label="Search disease by name or CUI"
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm min-w-[220px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-sm border-collapse" role="grid" aria-label="Enrichment results table">
          <thead>
            <tr className="border-b-2 border-gray-400 bg-gray-100">
              {showCheckboxes && (
                <th className="text-left py-2 px-3 font-bold border-r border-gray-300 w-10">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={handleSelectAll}
                      aria-label="Select all diseases on this page for graph"
                      className="rounded border-gray-300"
                    />
                    <span className="sr-only">Select all on page</span>
                  </label>
                </th>
              )}
              {plainHeader('Disease/Phenotype', 'left', false, diseaseColumnStyle)}
              {sortableHeader('pvalue', 'p-value', 'right')}
              {plainHeader('FDR Q-value', 'right')}
              {plainHeader('-log10(p)', 'right')}
              {plainHeader('bgRatio', 'right')}
              {sortableHeader('oddsRatio', 'oddsRatio', 'right')}
              {plainHeader(ratioHeaderLabel, 'right')}
              {sortableHeader('intersection', 'Intersection', 'right', true)}
            </tr>
          </thead>
          <tbody>
            {currentItems.length === 0 && (
              <tr>
                <td colSpan={showCheckboxes ? 9 : 8} className="py-8 text-center text-sm text-gray-500">
                  {diseaseQuery.trim()
                    ? `No diseases match “${diseaseQuery.trim()}”.`
                    : 'No enrichment results.'}
                </td>
              </tr>
            )}
            {currentItems.map((item, index) => {
              const ident = getDiseaseIdent(item);
              const canNavigate = !!ident;
              const diseaseClickable = isInteractive && canNavigate;
              const intersectionClickable = isInteractive && canNavigate;

              return (
                <tr
                  key={item.diseaseUMLSCUI || item.id || `${item.diseaseName}-${indexOfFirstItem + index}`}
                  className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  {showCheckboxes && (
                    <td className="py-2 px-3 border-r border-gray-200">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSet.has(item.id)}
                          onChange={() => handleSelectRow(item)}
                          aria-label={`Select ${item.diseaseName} for graph`}
                          className="rounded border-gray-300"
                        />
                      </label>
                    </td>
                  )}
                  <td
                    className="py-2 px-2 text-left border-r border-gray-200"
                    style={diseaseColumnStyle}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0 border border-gray-400"
                        style={{ backgroundColor: item.color }}
                        aria-hidden
                      />
                      {diseaseClickable ? (
                        <button
                          type="button"
                          onClick={() => handleDiseaseClick(item)}
                          data-demo-tour="enrichment-disease-button"
                          className="min-w-0 truncate text-left text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-0.5"
                          title={`View Disease Summary: ${item.diseaseName}`}
                        >
                          {item.diseaseName}
                        </button>
                      ) : (
                        <span className="min-w-0 truncate" title={item.diseaseName}>
                          {item.diseaseName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {Number(item.pvalue).toExponential(2)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {item.fdrQValue != null ? Number(item.fdrQValue).toExponential(2) : '—'}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {(-Math.log10(item.pvalue)).toFixed(2)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {isDownload ? Number(item.bgRatioNum).toFixed(4) : Number(item.bgRatioNum).toFixed(6)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {Number(item.oddsRatioNum).toFixed(2)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {isVariantEnrichment ? item.variantRatio?.trim() || '—' : item.geneRatioValue}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">
                    {intersectionClickable ? (
                      <button
                        type="button"
                        onClick={() => handleIntersectionClick(item)}
                        data-demo-tour="enrichment-intersection-button"
                        className="text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-1"
                        title={`View Disease Summary: ${item.diseaseName}`}
                      >
                        {item.intersectionSize}
                      </button>
                    ) : (
                      item.intersectionSize
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!isDownload && filteredData.length > 0 && (
        <div className="flex flex-wrap justify-between items-center gap-3 mt-4 text-sm text-gray-600">
          <span>
            Showing {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, filteredData.length)} of{' '}
            {filteredData.length}
          </span>

          {totalPages > 1 && (
            <nav className="flex items-center gap-2" aria-label="Table pagination">
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={safePage === 1}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Previous page"
              >
                Prev
              </button>

              <span className="px-2 py-1" aria-live="polite">
                Page {safePage} of {totalPages}
              </span>

              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={safePage === totalPages}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Next page"
              >
                Next
              </button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default DataTable;
